/**
 * Renders "How Fomo-Fi works" when something has opened it (doc 3 E1, E2), and saves
 * where it was closed (E10, G2, G5). Mounted once in App. It's a portal over the page,
 * so nothing underneath unmounts: form fields, a draft plan and import progress keep
 * their state (E2).
 *
 * It also publishes the saved step that "Continue how it works (step N of 10)" reads
 * (C9): the account's `walkthrough_last_step` when signed in, this browser's before there
 * is an account. And it opens on `openWalkthrough(step, from)` from the bridge
 * (lib/walkthroughBridge), the sign-up pages' entry point.
 */
import { useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useProfile, useUpdateProfile } from "@/hooks/use-profile";
import { closeTourOutcome, readAnonStep, writeAnonStep } from "@/lib/walkthrough";
import {
  WALKTHROUGH_OPEN_EVENT,
  openFromBridge,
  type WalkthroughOpenDetail,
} from "@/lib/walkthroughBridge";
import {
  closeWalkthroughSession,
  getWalkthroughSavedStep,
  setWalkthroughSavedStep,
  useWalkthroughSession,
} from "@/lib/walkthroughStore";
import { WalkthroughDialog } from "./WalkthroughDialog";

export function WalkthroughHost() {
  const session = useWalkthroughSession();
  const { isSignedIn, isLoaded } = useAuth();
  const signedIn = isLoaded === true && isSignedIn === true;
  const profile = useProfile({ enabled: signedIn });
  const update = useUpdateProfile();
  const accountStep = profile.data?.profile.walkthrough_last_step ?? null;

  useEffect(() => {
    setWalkthroughSavedStep(signedIn ? accountStep : readAnonStep());
  }, [signedIn, accountStep]);

  useEffect(() => {
    const onOpen = (e: Event) => openFromBridge((e as CustomEvent<WalkthroughOpenDetail>).detail);
    window.addEventListener(WALKTHROUGH_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(WALKTHROUGH_OPEN_EVENT, onOpen);
  }, []);

  if (!session) return null;

  // Addendum R6 (the prototype's closeTour): a close saves the step only past where this
  // opener starts and before step 10; otherwise the saved step stays as it was, and
  // nothing is written. Finishing step 10 clears it and stamps completion (R4, G5).
  const persist = (step: number, finished: boolean) => {
    const { lastStep, completed, changed } = closeTourOutcome({
      step,
      startStep: session.startStep ?? session.step,
      savedStep: getWalkthroughSavedStep(),
      finished,
    });
    if (!changed) return;
    setWalkthroughSavedStep(lastStep);
    if (signedIn) {
      update.mutate({
        profile: completed
          ? { walkthrough_completed_at: new Date().toISOString() }
          : { walkthrough_last_step: lastStep },
      });
    } else {
      writeAnonStep(lastStep);
    }
  };

  return (
    <WalkthroughDialog
      key={`${session.from}:${session.step}`}
      initialStep={session.step}
      from={session.from}
      onClose={(step, finished) => {
        persist(step, finished);
        closeWalkthroughSession();
      }}
    />
  );
}
